import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { parse, parseISO, startOfDay } from 'date-fns';
import FlatfileImporter from 'flatfile-csv-importer';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { filter, switchMap, takeUntil, tap } from 'rxjs/operators';
import { FlatfileField, FlatFileService } from '../../shared/flat-file.service';
import { fieldToNumber } from '../../shared/flatFileHooks';
import { SnackBarService } from '../../shared/snackbar.service';
import { Experience } from '../../types';
import {
  CapacityManagement,
  CapacityManagementDate,
  CarrierCommitmentsService,
  Region,
  Subcontractor,
} from '../carrier-commitments.service';
import { ExperienceService } from '../experience-manager/experience.service';

interface DateValue {
  date: string;
  carriers: CarrierValue[];
  orderCount: number;
  orderCountPerShift: number;
}

interface CarrierValue {
  carrierID: string;
  shiftCount: number;
}

export const orderCountFields: FlatfileField[] = [
  {
    label: 'Date',
    key: 'date',
    alternates: ['date', 'dateTime'],
    validators: [
      {
        validate: 'required',
        error: 'Date is required',
      },
    ],
  },
  {
    label: 'Order Count',
    key: 'orderCount',
    alternates: ['OrderCount', 'Total Orders'],
    validators: [
      {
        validate: 'required',
        error: 'Order Count is required',
      },
    ],
  },
];

export interface RawOrderCount {
  date: string;
  orderCount: number;
}

@Component({
  selector: 'lohi-carrier-commitments',
  templateUrl: './carrier-commitments.component.html',
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CarrierCommitmentsComponent implements OnInit, OnDestroy {
  public regionControl = new FormControl(null);
  public startControl = new FormControl(null);
  public endControl = new FormControl(null);
  public subControl = new FormControl(null);
  public percentControl = new FormControl(null);
  public turnRateControl = new FormControl(null);
  public experienceControl = new FormControl(null);
  public subcontractors$$ = new BehaviorSubject<Subcontractor[]>([]);
  public regions$: Observable<Region[]>;
  public experiences$: Observable<Experience[]>;
  public dates: FormGroup[];
  public destroy$$ = new Subject();
  public estimatedOrders$$ = new BehaviorSubject<number>(null);
  public fileUpload: FlatfileImporter;

  constructor(
    private carrierCommitments: CarrierCommitmentsService,
    private experienceServices: ExperienceService,
    private fb: FormBuilder,
    private snackBar: SnackBarService,
    private cd: ChangeDetectorRef,
    private flatFile: FlatFileService,
    private router: Router,
  ) {}

  public ngOnInit(): void {
    this.setupUpload();
    this.regions$ = this.carrierCommitments.regions$;
    this.experiences$ = this.experienceServices.experiences$;
    combineLatest([this.regionControl.valueChanges, this.startControl.valueChanges, this.endControl.valueChanges])
      .pipe(
        filter(([region, start, end]) => {
          return !!region && !!start && !!end;
        }),
        switchMap(([region, start, end]) => {
          return this.carrierCommitments.getCarrierCapacity(region, start, end);
        }),
        tap((x) => {
          this.dates = [];
          if (x === null) {
            return;
          }
          const newFormArray: FormGroup[] = [];
          if (x.capacityManagement.subcontractors === null || x.capacityManagement.subcontractors.length === 0) {
            this.estimatedOrders$$.next(null);
            this.subcontractors$$.next([]);
            this.snackBar.showMessage('No subcontractors');
            return;
          }
          this.estimatedOrders$$.next(x.capacityManagement.estimatedOrdersPerShift);
          this.subcontractors$$.next(x.capacityManagement.subcontractors);
          for (const data of x.capacityManagement.dates) {
            const carriers: FormGroup[] = [];
            for (const shiftCount of x.capacityManagement.subcontractors) {
              let commitment = null;
              if (data.subcontractorShiftCounts !== null) {
                for (const shiftCount1 of data.subcontractorShiftCounts) {
                  if (shiftCount1.subcontractorID === shiftCount.id) {
                    commitment = shiftCount1.shiftCount;
                  }
                }
              }
              carriers.push(
                this.fb.group({
                  carrierID: [shiftCount.id],
                  shiftCount: [commitment],
                  carrierName: [shiftCount.name],
                }),
              );
            }
            newFormArray.push(
              this.fb.group({
                date: [data.date],
                carriers: this.fb.array(carriers),
                orderCount: [data.orderCount, [Validators.required]],
                orderCountPerShift: [data.ordersPerShift, [Validators.required]],
              }),
            );
          }
          this.dates = newFormArray;
          this.cd.markForCheck();
        }),
        takeUntil(this.destroy$$),
      )
      .subscribe();
  }

  public setPercentages(formArray: FormArray, percent: number) {
    for (const control of formArray.controls) {
      control.get('shiftCount').setValue(percent);
    }
  }

  public updatePercentages() {
    if (!this.subControl.value || this.percentControl.value === null) {
      return;
    }
    for (const date of this.dates) {
      for (const control of (date.get('carriers') as FormArray).controls) {
        if (control.value.carrierID === this.subControl.value) {
          control
            .get('shiftCount')
            .setValue(
              Math.ceil((date.value.orderCount / date.value.orderCountPerShift) * (this.percentControl.value / 100)),
            );
        }
      }
    }
  }

  public updateTurnRate() {
    if (this.turnRateControl.value === null) {
      return;
    }
    for (const date of this.dates) {
      date.get('orderCountPerShift').setValue(this.turnRateControl.value);
    }
  }

  public getSum(formArray: FormArray) {
    let total = 0;
    for (const control of formArray.controls) {
      total += control.get('shiftCount').value;
    }
    return total;
  }

  public async promptFileUpload() {
    try {
      const flatFileData = await this.fileUpload.requestDataFromUser();
      const data = flatFileData.data as RawOrderCount[];
      for (const date of this.dates) {
        const value = date.value as DateValue;
        const parsedDate = parseISO(value.date);
        for (const datum of data) {
          if (parsedDate.getUTCDate() === startOfDay(parse(datum.date, 'M/d/yyyy', new Date())).getUTCDate()) {
            date.get('orderCount').setValue(datum.orderCount);
            break;
          }
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      this.fileUpload.close();
    }
  }

  public async submit() {
    if (this.regionControl.value === null || this.startControl.value === null || this.endControl.value === null) {
      return;
    }
    let anyInvalid = false;
    for (const date of this.dates) {
      if (date.invalid) {
        date.markAllAsTouched();
        anyInvalid = true;
      }
    }

    if (anyInvalid) {
      return;
    }

    const datez: CapacityManagementDate[] = [];

    for (const date of this.dates) {
      const value = date.value as DateValue;
      datez.push({
        date: value.date,
        orderCount: value.orderCount,
        ordersPerShift: value.orderCountPerShift,
        subcontractorShiftCounts: value.carriers
          .filter((value1) => value1.shiftCount !== null)
          .map((value1) => {
            return {
              shiftCount: Math.round(value1.shiftCount),
              subcontractorID: value1.carrierID,
            };
          }),
      });
    }

    const exp: string = this.experienceControl.value;
    const toSend: CapacityManagement = {
      dates: datez,
      regionId: this.regionControl.value,
      experienceId: exp && exp.length ? exp : null,
    };
    try {
      await this.carrierCommitments.updateCapacity(toSend);
      this.snackBar.showMessage('Capacity Updated');
      this.router.navigateByUrl('/internal');
    } catch (e) {
      console.error(e);
    }
  }

  public ngOnDestroy(): void {
    this.destroy$$.next();
    this.destroy$$.unsubscribe();
  }

  private async setupUpload() {
    this.fileUpload = await this.flatFile.getImporter(orderCountFields, 'Order Counts Per Day');
    this.fileUpload.registerFieldHook('orderCount', fieldToNumber);
  }
}
